import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Formik } from 'formik';
import { useCommunityContext } from 'providers';
import { Fragment } from 'react';

import {
  CreateCommunityProductMutationVariables,
  useCreateCommunityProductMembershipMutation,
  useCreateCommunityProductMutation,
} from 'shared/graphql';
import { useToast } from 'shared/hooks';
import { recordDebug } from 'shared/utils/record';

import {
  DEFAULT_COMMUNITY_PRODUCT_FORM_VALUES,
  CommunityProductForm,
  CommunityProductFormValues,
} from '../../components/CommunityProductForm';

const SAVING_MESSAGE_KEY = 'SAVING_SETTINGS_COMMUNITY_PRODUCTS';

interface Props {
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;

  /**
   * Callback to close the modal.
   */
  onClose: () => void;
}

export function CreateCommunityProductModal(props: Props) {
  const { showMessage, showSuccess, showError, dismiss } = useToast();
  const { selectedCommunity: community } = useCommunityContext();

  const [createMembership] = useCreateCommunityProductMembershipMutation({
    refetchQueries: ['ListCommunityProductMemberships'],
  });

  const [createProduct] = useCreateCommunityProductMutation({
    refetchQueries: ['ListCommunityProducts'],
    awaitRefetchQueries: true,
  });

  const handleSubmit = async (values: CommunityProductFormValues) => {
    if (!community?.id) {
      showError('Community not found');
      return;
    }

    try {
      showMessage(`Saving product, please stand by...`, {
        id: SAVING_MESSAGE_KEY,
      });

      const logo = values.images[0];

      const { data: membershipResponse } = await createMembership({
        variables: {
          community: community?.id,
          name: values.name,
          description: values.description,
          logo: logo?.id,
          color: values.color,
        },
      });

      if (!membershipResponse || !membershipResponse.membership) {
        throw new Error('Failed to create membership');
      }

      const { membership } = membershipResponse;

      recordDebug(`Membership created with ID: ${membership.id}`);

      const payload: CreateCommunityProductMutationVariables = {
        community: community?.id,
        name: values.name,
        description: values.description,
        priceCents: values.priceCents,
        type: values.type,
        image: logo?.id,
        frequency: values.frequency,
        membership: membership.id,
      };

      await createProduct({ variables: payload });

      showSuccess(`Successfully created product "${values.name}"`);
      props.onClose();
    } catch (err) {
      if (err instanceof Error) {
        showError(err.message);
      }
    } finally {
      dismiss(SAVING_MESSAGE_KEY);
    }
  };

  return (
    <Dialog maxWidth="sm" open={props.isOpen} onClose={props.onClose} fullWidth>
      <DialogTitle>Create Product / Membership</DialogTitle>

      <Formik initialValues={DEFAULT_COMMUNITY_PRODUCT_FORM_VALUES} onSubmit={handleSubmit}>
        {form => (
          <Fragment>
            <DialogContent dividers>
              <CommunityProductForm />
            </DialogContent>

            <DialogActions>
              <Button variant="outlined" disabled={form.isSubmitting} onClick={props.onClose}>
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={form.isSubmitting || !form.dirty}
                onClick={form.submitForm}
              >
                Save
              </Button>
            </DialogActions>
          </Fragment>
        )}
      </Formik>
    </Dialog>
  );
}
